'use client';

import { useEffect, useState } from 'react';

// sessionStorage key used to prevent an infinite reload loop during migration.
// Set before the migration reload; cleared after registration succeeds.
const MIGRATION_FLAG = 'sw-migration-done';
const DEV_CLEANUP_FLAG = 'sw-dev-cleanup-done';

async function cleanupInDevelopment(): Promise<void> {
  if (sessionStorage.getItem(DEV_CLEANUP_FLAG)) return;

  const registrations = await navigator.serviceWorker.getRegistrations();
  if (registrations.length > 0) {
    await Promise.all(registrations.map((r) => r.unregister()));
    console.log(`[SW] Dev cleanup: unregistered ${registrations.length} service worker(s).`);
  }

  if ('caches' in window) {
    const keys = await caches.keys();
    const swRelated = keys.filter((k) =>
      ['ganeshait-', 'serwist', 'workbox', 'precache'].some((prefix) => k.includes(prefix)),
    );

    if (swRelated.length > 0) {
      await Promise.all(swRelated.map((k) => caches.delete(k)));
      console.log(`[SW] Dev cleanup: deleted ${swRelated.length} SW cache bucket(s).`);
    }
  }

  sessionStorage.setItem(DEV_CLEANUP_FLAG, '1');
}

/**
 * One-time migration: unregisters ALL existing service workers and wipes ALL
 * caches, then reloads the page.
 *
 * Why this approach:
 *   The old hand-written SW (ganeshait-static-v1 etc.) is the ACTIVE controller
 *   when the page loads. It intercepts fetches BEFORE any of our JS runs, so
 *   simply deleting caches is too late — the old SW can re-serve or re-populate
 *   them. The only reliable fix is to (1) unregister every SW, (2) delete every
 *   cache entry, and (3) hard-reload so the NEXT page load has no SW at all and
 *   the server response is used directly.
 */
async function runMigration(): Promise<void> {
  console.log('[SW] Legacy SW detected — starting migration.');

  // Unregister every SW registration (old hand-written SW + any stale copies).
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((r) => r.unregister()));
  console.log(`[SW] Unregistered ${registrations.length} service worker(s).`);

  // Delete every cache bucket regardless of name.
  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
    console.log(`[SW] Deleted ${keys.length} cache bucket(s).`);
  }

  // Mark migration complete so the reload doesn't trigger this again.
  sessionStorage.setItem(MIGRATION_FLAG, '1');

  // Reload with no SW and empty caches → browser fetches everything fresh.
  window.location.reload();
}

/**
 * Returns true if an existing SW registration is present AND the migration
 * has not already run in this browser session.
 */
async function needsMigration(): Promise<boolean> {
  // Already ran this session — don't loop.
  if (sessionStorage.getItem(MIGRATION_FLAG)) return false;

  const registrations = await navigator.serviceWorker.getRegistrations();
  if (registrations.length === 0) return false;

  // Check whether any active/waiting/installing SW is the old hand-written one
  // (identified by a cache key it would have created).
  if ('caches' in window) {
    const keys = await caches.keys();
    const hasOldCache = keys.some((k) =>
      ['ganeshait-static', 'ganeshait-pages', 'ganeshait-images', 'ganeshait-api'].some(
        (p) => k.startsWith(p),
      ),
    );
    if (hasOldCache) return true;
  }

  return false;
}

/**
 * Registers the Serwist-generated service worker and handles the full update
 * lifecycle:
 *
 *  Migration     Old SW detected on first load → all SWs unregistered, all
 *                caches wiped, page reloads clean with no controller.
 *
 *  Scenario A.1/A.2  Cold start or refresh while online → NetworkFirst always
 *                    hits the network first; no toast shown.
 *
 *  Scenario B.1  New deployment detected while tab is open → "Update
 *                Available" toast (no forced reload, safe for active forms).
 *
 *  Scenario B.2  User clicks "Update Now" → SW activates → page reloads once.
 *
 *  Scenario C.1  Offline navigation → SW serves cached page or offline.html.
 *
 *  Scenario C.2  Connection restored → Serwist's reloadOnOnline handles this
 *                automatically (configured via next.config.ts).
 */
export function ServiceWorkerRegistration() {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !('serviceWorker' in navigator)
    ) {
      return;
    }

    if (process.env.NODE_ENV !== 'production') {
      cleanupInDevelopment().catch(() => {
        // Best-effort cleanup only; do not block the app in development.
      });
      return;
    }

    let reloading = false;

    const trackInstalling = (reg: ServiceWorkerRegistration) => {
      const installing = reg.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        // 'installed' + existing controller → new SW is waiting
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          setUpdateAvailable(true);
        }
      });
    };

    const register = async () => {
      try {
        // ── Step 1: One-time migration check.
        // If the old hand-written SW is still active, wipe everything and
        // reload. The sessionStorage flag prevents re-running after the reload.
        if (await needsMigration()) {
          await runMigration();
          return; // execution stops here; page reload is in progress
        }

        // Clear the migration flag now that we're past it.
        sessionStorage.removeItem(MIGRATION_FLAG);

        // ── Step 2: Register the Serwist SW (no-op in dev — Serwist is
        // disabled via `disable: NODE_ENV === 'development'` in next.config.ts,
        // so /sw.js returns 404 in dev and the catch below handles it silently).
        const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });

        // ── Step 3: SW already waiting when this tab opened
        if (reg.waiting && navigator.serviceWorker.controller) {
          setUpdateAvailable(true);
        }

        // ── Step 4: SW update found while tab is open
        reg.addEventListener('updatefound', () => trackInstalling(reg));

        // ── Step 5: Poll for updates every hour (long-lived tabs)
        setInterval(() => reg.update(), 60 * 60 * 1000);
      } catch {
        // SW is unavailable or dev mode — site works fine online without one.
      }
    };

    // Reload once the new SW takes control after SKIP_WAITING.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });

    if (document.readyState === 'complete') {
      register();
    } else {
      window.addEventListener('load', register, { once: true });
    }
  }, []);

  const handleUpdate = () => {
    navigator.serviceWorker.getRegistration().then((reg) => {
      reg?.waiting?.postMessage({ type: 'SKIP_WAITING' });
    });
  };

  // ── Update-available toast (Scenario B.1 / B.2) ──────────────────────────
  if (!updateAvailable) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-50 flex items-center gap-3 rounded-xl border border-slate-700 bg-slate-800 px-4 py-3 shadow-2xl"
    >
      <div className="flex flex-col">
        <span className="text-sm font-semibold text-slate-100">Update Available</span>
        <span className="text-xs text-slate-400">A new version of this app is ready.</span>
      </div>
      <button
        onClick={handleUpdate}
        className="ml-2 shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        Update Now
      </button>
    </div>
  );
}
